import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DirectionsBusOutlinedIcon from '@mui/icons-material/DirectionsBusOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import {
  cancelRulesDuty,
  fetchRulesDrivers,
  fetchRulesDuties,
  validateAndPersistDuty,
} from '../../features/rulesEngine/api';
import type { RulesDriver, RulesDuty, ValidationResultV1 } from '../../features/rulesEngine/types';
import RulesSectionCard from './RulesSectionCard';

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DutiesPanel() {
  // Temporary: soft override available to all signed-in planners.
  const canOverride = true;

  const [drivers, setDrivers] = useState<RulesDriver[]>([]);
  const [duties, setDuties] = useState<RulesDuty[]>([]);
  const [driverId, setDriverId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [requiredTags, setRequiredTags] = useState('');
  const [startAt, setStartAt] = useState(() => toLocalInputValue(new Date()));
  const [endAt, setEndAt] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 8);
    return toLocalInputValue(d);
  });
  const [overrideReason, setOverrideReason] = useState('');
  const [validation, setValidation] = useState<ValidationResultV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const [d, dutyList] = await Promise.all([fetchRulesDrivers(), fetchRulesDuties()]);
    setDrivers(d.filter((x) => x.status === 'active'));
    setDuties(dutyList);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [reload]);

  const softFails = useMemo(
    () => (validation?.results || []).filter((r) => r.status === 'fail' && r.severity === 'soft'),
    [validation]
  );
  const hardFails = useMemo(
    () => (validation?.results || []).filter((r) => r.status === 'fail' && r.severity === 'hard'),
    [validation]
  );
  const driverNameById = useMemo(
    () => Object.fromEntries(drivers.map((d) => [d.id, d.display_name])),
    [drivers]
  );

  const onSave = async (withOverrides: boolean) => {
    setBusy(true);
    setError(null);
    setValidation(null);
    try {
      const startIso = new Date(startAt).toISOString();
      const endIso = new Date(endAt).toISOString();
      const tags = requiredTags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const overrides =
        withOverrides && softFails.length && overrideReason.trim()
          ? softFails.map((r) => ({ ruleId: r.ruleId, reason: overrideReason.trim() }))
          : [];

      const result = await validateAndPersistDuty({
        driver_id: driverId || null,
        vehicle_id: vehicleId.trim() || null,
        start_at: startIso,
        end_at: endIso,
        required_vehicle_tags: tags,
        overrides,
        persist: true,
      });
      setValidation(result);
      if (result.overallStatus === 'fail') {
        setError('Hard validation failures blocked save.');
        return;
      }
      if (result.overallStatus === 'warn' && !withOverrides) {
        setError('Soft warnings present — enter a reason and use Override & save.');
        return;
      }
      await reload();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = ax.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail !== null && 'overallStatus' in (detail as object)) {
        setValidation(detail as ValidationResultV1);
        setError('Validation blocked save.');
      } else {
        setError(e instanceof Error ? e.message : 'Save failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const validationSeverity =
    validation?.overallStatus === 'pass'
      ? 'success'
      : validation?.overallStatus === 'warn'
        ? 'warning'
        : 'error';

  return (
    <Box data-testid="rules.duties.panel" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Duties
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Plan a single duty against the active rule set. Hard failures block save; soft warnings need
          an override reason.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <RulesSectionCard
        icon={<EventNoteOutlinedIcon />}
        title="Plan a duty"
        description="Choose driver, vehicle, window, and any required capability tags, then validate."
      >
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel id="duty-driver-label">Driver</InputLabel>
            <Select
              labelId="duty-driver-label"
              label="Driver"
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              data-testid="rules.duties.driver-select"
            >
              <MenuItem value="">— none —</MenuItem>
              {drivers.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Vehicle id"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <DirectionsBusOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              ),
            }}
            data-testid="rules.duties.vehicle-input"
          />
          <TextField
            size="small"
            label="Required vehicle tags"
            placeholder="e.g. accessible"
            value={requiredTags}
            onChange={(e) => setRequiredTags(e.target.value)}
            fullWidth
            helperText="Comma-separated. Vehicle must carry all of these."
            InputProps={{
              startAdornment: (
                <LocalOfferOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              ),
            }}
            data-testid="rules.duties.tags-input"
            sx={{ gridColumn: { md: '1 / -1' } }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label="Start"
            InputLabelProps={{ shrink: true }}
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <ScheduleOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              ),
            }}
            data-testid="rules.duties.start-input"
          />
          <TextField
            size="small"
            type="datetime-local"
            label="End"
            InputLabelProps={{ shrink: true }}
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <ScheduleOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              ),
            }}
            data-testid="rules.duties.end-input"
          />
        </Box>

        {canOverride && softFails.length > 0 && (
          <TextField
            size="small"
            label="Soft override reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            fullWidth
            sx={{ mt: 1.5 }}
            helperText="Required to save when soft rules fail."
            InputProps={{
              startAdornment: (
                <WarningAmberOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
              ),
            }}
            data-testid="rules.duties.override-reason"
          />
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => void onSave(false)}
            disabled={busy}
            startIcon={<SaveOutlinedIcon />}
            data-testid="rules.duties.save-btn"
          >
            Validate & save
          </Button>
          {canOverride && (
            <Button
              variant="outlined"
              onClick={() => void onSave(true)}
              disabled={busy || !overrideReason.trim()}
              startIcon={<GavelOutlinedIcon />}
              data-testid="rules.duties.override-save-btn"
            >
              Override & save
            </Button>
          )}
        </Stack>
      </RulesSectionCard>

      {validation && (
        <RulesSectionCard
          icon={<FactCheckOutlinedIcon />}
          title="Validation result"
          description="Failures from the active rule set. Hard blocks save; soft can be overridden with a reason."
          action={
            <Chip
              size="small"
              color={
                validation.overallStatus === 'pass'
                  ? 'success'
                  : validation.overallStatus === 'warn'
                    ? 'warning'
                    : 'error'
              }
              variant="outlined"
              icon={
                validation.overallStatus === 'pass' ? (
                  <CheckCircleOutlineIcon />
                ) : validation.overallStatus === 'warn' ? (
                  <WarningAmberOutlinedIcon />
                ) : (
                  <LockOutlinedIcon />
                )
              }
              label={validation.overallStatus}
            />
          }
        >
          <Alert severity={validationSeverity} data-testid="rules.duties.validation">
            {(hardFails.length === 0 && softFails.length === 0 && (
              <Typography variant="body2">All checked rules passed.</Typography>
            )) || (
              <Stack spacing={0.75} component="ul" sx={{ m: 0, pl: 2 }}>
                {validation.results
                  .filter((r) => r.status === 'fail')
                  .map((r) => (
                    <Box component="li" key={r.ruleId}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <Chip
                          size="small"
                          variant="outlined"
                          color={r.severity === 'hard' ? 'error' : 'warning'}
                          icon={
                            r.severity === 'hard' ? (
                              <LockOutlinedIcon />
                            ) : (
                              <WarningAmberOutlinedIcon />
                            )
                          }
                          label={r.severity}
                        />
                        <Typography variant="body2">{r.message}</Typography>
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            )}
          </Alert>
        </RulesSectionCard>
      )}

      <RulesSectionCard
        icon={<ListAltOutlinedIcon />}
        title="Saved duties"
        description="Active duties on this customer. Cancel removes them from planning."
        action={
          duties.length > 0 ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<EventNoteOutlinedIcon />}
              label={duties.length === 1 ? '1 duty' : `${duties.length} duties`}
            />
          ) : null
        }
      >
        <Table size="small" data-testid="rules.duties.table">
          <TableHead>
            <TableRow>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {duties.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{new Date(d.start_at).toLocaleString()}</TableCell>
                <TableCell>{new Date(d.end_at).toLocaleString()}</TableCell>
                <TableCell>
                  {(d.driver_id && driverNameById[d.driver_id]) || d.driver_id || '—'}
                </TableCell>
                <TableCell sx={{ fontFamily: d.vehicle_id ? 'monospace' : undefined }}>
                  {d.vehicle_id || '—'}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<CancelOutlinedIcon />}
                    onClick={() => void cancelRulesDuty(d.id).then(reload)}
                    data-testid={`rules.duties.cancel.${d.id}`}
                  >
                    Cancel
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!duties.length && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No active duties. Plan one above or accept a roster proposal.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </RulesSectionCard>
    </Box>
  );
}
